import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { bp } from '../../../styles/breakPoints.tsx';

import TopRightCorner from '../../../assets/images/corner_top_right.svg';
import BottomRightCorner from '../../../assets/images/corner_bottom_right.svg';
import ResizedImage from '../ResizedImage.tsx';
import { Color, Image } from '../../types.ts';
import { useInlineHeadAsset } from '@hubspot/cms-components';


interface KeyPoint {
  icon: Image;
  text: string;
}
interface HeroProps {
  fieldValues: {
    main_title: string;
    main_image: Image;
    box_content: string;
    box_background_color: Color;
    box_text_color: Color;
    key_points: KeyPoint[];
  };
}

const StyledHero = styled.div<{ $backgroundColor: Color; $textColor: Color }>`

  .hero {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .image-container {
    display: flex;
    border-radius: 0;
    overflow: hidden;
    width: 100%;

    @media ${bp.md} {
      border-radius: 16px;
    }
    img:not(.corner) {
      width: 100%;
      object-fit: cover;
      object-position: 50% 0;
      min-height: 250px;
      max-height: 300px;

      @media ${bp.md} {
        min-height: 350px;
        max-height: 450px;
        aspect-ratio: 1376/436;
      }
    }
  }

  .title-container {
    position: absolute;
    top: 36px;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;

    @media ${bp.lg} {
      top: 25%;
      transform: translate(-50%, -50%);
      width: 60%;
    }

    @media ${bp.xl} {
      top: 30%;
    }

    h1 {
      white-space: normal;
      text-align: center;
      color: var(--Pvrit-Lumme, #004107);
      font-size: 24px;

      @media ${bp.md} {
        font-size: 56px;
      }
    }
  }

  .box-container {
    background-color: transparent;
    padding: 16px 16px 0;
    margin-top: -100px;
    margin-bottom: 20px;
    border-radius: 16px;
    width: 100%;
    position: relative;

    @media ${bp.md} {
      background-color: #fff;
      width: initial;
      max-width: 80%;

      margin-top: -160px;
    }

    .box {
      padding: 24px;
      background-color: ${(props) => props.$backgroundColor.css ?? 'green'};
      color: ${(props) => props.$textColor.css ?? 'black'};
      border-radius: 16px;
    }

    .corner {
      position: absolute;
      top: 84px;
      transform: rotate(90deg);
      display: none;

      @media ${bp.md} {
        display: block;
        top: 144px;
      }
    }

    .top-right {
      left: -16px;
    }

    .bottom-right {
      right: -16px;
    }
  }

  .key-points-container {
    display: flex;
    justify-content: center;
    margin: 10px 0;
  }

  .key-points {
    display: flex;
    gap: 16px;
    justify-content: flex-start;
    margin-bottom: 10px;
    flex-direction: column;
    margin: 0 20px;

    @media ${bp.md} {
      flex-direction: row;
      justify-content: center;
      flex-wrap: wrap;
    }

    .key-point {
      display: flex;
      gap: 6px;
      font-size: 14px;
      font-weight: 500;
      color: var(--Pvrit-Lumme, #004107);
      align-items: center;

      svg {
        width: 24px;
        height: 24px;
      }

      .icon {
        width: 50px;
        height: 24px;
        display: flex;
        justify-content: flex-end;

        img {
          max-width: initial;
        }
      }
    }
  }
`;

function FrontpageHeroB2C(props: HeroProps) {
  const { main_image, main_title, box_background_color, box_text_color, box_content, key_points } = props.fieldValues;

  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const imageContainer = document.getElementById('b2c-hero-image-container');
      if (imageContainer) {
        setImageWidth(imageContainer.clientWidth);
        setImageHeight(imageContainer.clientHeight);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useInlineHeadAsset(() => {
    return (
      <link rel="preload" href={main_image.src} as="image" fetchPriority='high' />
    )
  })

  return (
    <StyledHero
      $backgroundColor={box_background_color}
      $textColor={box_text_color}>
      <div className='hero'>
        <div className="image-container" id="b2c-hero-image-container">
          <ResizedImage src={main_image.src} width={imageWidth} height={imageHeight} alt={main_image.alt} loading='eager' />
        </div>
        <div className='title-container'>
          <h1>{main_title}</h1>
        </div>
        <div className='box-container' id="b2c-hero-box-container">
          <div className='box'>
            <div dangerouslySetInnerHTML={{ __html: box_content }}></div>
          </div>
          <img src={TopRightCorner} className='corner top-right' width={16} height={16} />
          <img src={BottomRightCorner} className='corner bottom-right' width={16} height={16} />
        </div>
      </div>
      <div className='key-points-container'>
        <div className='key-points'>
          {key_points.map((point, index) => (
            <div key={index} className='key-point'>
              <div className='icon'>
                <img src={point.icon.src} width={point.icon.width} height={point.icon.height} />
              </div>
              <span dangerouslySetInnerHTML={{ __html: point.text }}></span>
            </div>
          ))}
        </div>
      </div>
    </StyledHero>
  );
}

export default FrontpageHeroB2C;
